import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { demoDataGenerator } from '../../utils/demoDataGenerator';

const PreviewTreatmentAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Load demo data
  const demoData = useMemo(() => demoDataGenerator?.getTreatmentAnalytics(), []);
  const { treatments, metrics, fields } = demoData;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Preview Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Icon name="Eye" size={24} />
              <div>
                <h3 className="font-semibold text-lg">Preview Mode - Treatment Analytics</h3>
                <p className="text-sm text-blue-100">Sign up to track your actual treatment data and analytics</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="bg-white text-blue-600 hover:bg-blue-50 border-white"
                onClick={() => navigate('/pricing-page')}
              >
                View Plans
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => navigate('/login')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Treatment Analytics Dashboard</h1>
          <p className="text-gray-600 mt-2">Track treatment effectiveness, costs, and field performance</p>
        </div>

        {/* Metrics Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Treatments</span>
              <Icon name="Activity" size={20} className="text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{metrics?.totalTreatments}</div>
            <div className="text-sm text-green-600 mt-1">↑ 12% vs last month</div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Avg Effectiveness</span>
              <Icon name="TrendingUp" size={20} className="text-green-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{metrics?.avgEffectiveness}%</div>
            <div className="text-sm text-green-600 mt-1">↑ 5.2% improvement</div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Top Product</span>
              <Icon name="Award" size={20} className="text-yellow-600" />
            </div>
            <div className="text-lg font-bold text-gray-900">{metrics?.topProduct}</div>
            <div className="text-sm text-gray-500 mt-1">94.5% success rate</div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Cost</span>
              <Icon name="DollarSign" size={20} className="text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">${metrics?.totalCost?.toLocaleString()}</div>
            <div className="text-sm text-green-600 mt-1">↓ 8% cost reduction</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4 overflow-x-auto">
            {['overview', 'effectiveness', 'fields', 'costs']?.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 font-medium capitalize whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Treatment Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Treatments</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Treatment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effectiveness</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {treatments?.map(treatment => (
                  <tr key={treatment?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{treatment?.treatmentName}</div>
                      <div className="text-sm text-gray-500">{treatment?.applicationMethod}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{treatment?.product}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{treatment?.field}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{treatment?.applicationDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">{treatment?.effectiveness}%</div>
                        <div className={`ml-2 px-2 py-1 text-xs rounded-full ${
                          treatment?.effectiveness >= 90 ? 'bg-green-100 text-green-800' :
                          treatment?.effectiveness >= 80 ? 'bg-yellow-100 text-yellow-800': 'bg-red-100 text-red-800'
                        }`}>
                          {treatment?.effectiveness >= 90 ? 'Excellent' :
                           treatment?.effectiveness >= 80 ? 'Good' : 'Fair'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${treatment?.cost?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Field Performance */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Field Performance Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {fields?.map(field => (
              <div key={field?.id} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{field?.name}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Treatments:</span>
                    <span className="font-medium text-gray-900">{field?.treatments}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Avg Effectiveness:</span>
                    <span className="font-medium text-green-600">{field?.avgEffectiveness}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-8 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-8 border-2 border-blue-200">
          <div className="text-center max-w-2xl mx-auto">
            <Icon name="Sparkles" size={48} className="text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Track Your Own Data?</h3>
            <p className="text-gray-600 mb-6">
              Sign up for free to start tracking your treatments, analyze effectiveness, and optimize your IPM program with real-time analytics.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                onClick={() => navigate('/login')}
              >
                Start Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => navigate('/pricing-page')}
              >
                View All Features
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">✓ 14-day free trial • ✓ No credit card required • ✓ Cancel anytime</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewTreatmentAnalytics;